import React, { Component } from "react";
import { View, TouchableOpacity } from "react-native";

import StyledButton from "ae-base/Base/StyledButton";
import StyledText from "ae-base/Base/StyledText";
import Style from "ae-style";
import InputContainer from "./Layout/InputContainer";
import InputLabel from "./Layout/InputLabel";
import ErrorMessage from "./Layout/ErrorMessage";

import ResponsiveStylesheet from "react-native-responsive-stylesheet";
import Breakpoints from "react-native-dimensions-breakpoints";

import memoize from "memoizee";

import { useResources, useIdPrefix } from "@aetonix/hooks";

const styles = ResponsiveStylesheet.createSized("min-direction", {
	0: {
		alignCenter: {
			alignItems: "center"
		},
		circleButton: {
			justifyContent: "center",
			alignItems: "center",
			paddingHorizontal: 2,
			paddingVertical: 4,
			marginHorizontal: 2,
			flexGrow: 1,
		},
		selectedButton: {
			backgroundColor: Style.colors.primary
		},
		arrowDown: {
			width: 0,
			height: 0,
			borderLeftWidth: 8,
			borderRightWidth: 8,
			borderTopWidth: 8,
			borderStyle: "solid",
			borderLeftColor: "transparent",
			borderRightColor: "transparent"
		},
		arrowDownPrimary: {
			borderTopColor: Style.colors.primary
		},
		arrowDownAlert: {
			borderTopColor: Style.colors.alert
		},
		cursorRadius: {
			overflow: "hidden",
			borderRadius: 12
		},
		textCenter: {
			textAlign: "center"
		},
		rowSpace: {
			flexDirection: "row",
			justifyContent: "space-between"
		},
		values: {
			paddingHorizontal: 2,
			marginBottom: 8,
			flex: 8
		},
		label: {
			flex: 1
		},
		rangeContainer: {
			flexDirection: "row",
			flex: 1,
			alignItems: "center"
		}
	},
	512: {
		circleButton: {
			paddingHorizontal: 8
		},
		values: {
			paddingHorizontal: 14
		}
	}
});

const SCALE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

class RangeInputComp extends Component {
	constructor(props) {
		super(props);

		this.state = {
			hasChanged: false,
			value: props.defaultValue || 1
		};

		this.update = memoize(updateValue.bind(this));
	}
	render() {
		const props = this.props;
		const localize = props.localize;
		const localizeFrom = props.localizeFrom;
		const low = localize("atouchawayreminder.low");
		const high = localize("atouchawayreminder.high");
		const errors = props.errors;
		const schema = props.schema;
		const rightLabel = schema.rightLabel;
		const leftLabel = schema.leftLabel;
		const scale = schema.enum || SCALE;
		const min = scale[0];
		const max = scale[scale.length - 1];
		const isMobile = Breakpoints.smallerThan("mobile");

		// TODO: Have localization use min and max
		const touchanumberfrom1to10 = localize("atouchawayreminder.touchanumberfrom1to10", {
			min: min,
			max: max
		});

		const doUpdate = this.update;

		const defaultValue = this.state.hasChanged ? this.state.value : props.defaultValue !== undefined && props.defaultValue !== null  ? props.defaultValue : undefined;

		let renderRightLabel = null;
		let renderLeftLabel = null;

		const leftLabelText = leftLabel ? localizeFrom(leftLabel.localization, ".") : "";
		if (leftLabel) {
			renderLeftLabel = (
				<View style={styles.label}>
					<StyledText>{leftLabelText}</StyledText>
				</View>
			);
		}

		const rightLabelText = rightLabel ? localizeFrom(rightLabel.localization, ".") : "";
		if (rightLabel) {
			renderRightLabel = (
				<View style={styles.label}>
					<StyledText>{rightLabelText}</StyledText>
				</View>
			);
		}

		const renderTopButtons =
			leftLabel || rightLabel ? null : (
				<View style={styles.rowSpace}>
					<ScaleCursor title={low} contrast />
					<ScaleCursor title={high} alert />
				</View>
			);

		const renderScaleButtons = scale.map(function (number) {
			const selected = number == defaultValue;
			const onChange = doUpdate(number);
			return (
				<NumberButton
					id={props.id + "_" + number}
					key={number}
					number={number}
					selected={selected}
					onChange={onChange}
				/>
			);
		});

		const renderTouchANumberText =
			!schema.enum && !rightLabel && !leftLabel ? (
				<View style={styles.textCenter}>
					<StyledText bold black>
						{touchanumberfrom1to10}
					</StyledText>
				</View>
			) : null;

		let renderLabelsMobile = null;

		if (isMobile && (rightLabel || leftLabel)) {
			renderLeftLabel = null;
			renderRightLabel = null;
			const leftText = scale[0] + " = " + leftLabelText;
			const rightText = scale[scale.length - 1] + " = " + rightLabelText;
			renderLabelsMobile = (
				<View>
					<StyledText>{leftText}</StyledText>
					<StyledText>{rightText}</StyledText>
				</View>
			);
		}

		return (
			<InputContainer>
				<ErrorMessage id={props.id_prefix + props.id + "_error"} errors={errors} schema={schema} />
				<InputLabel schema={schema} />
				{renderTopButtons}
				<View style={styles.rangeContainer}>
					{renderLeftLabel}
					{renderScaleButtons}
					{renderRightLabel}
				</View>
				{renderTouchANumberText}
				{renderLabelsMobile}
			</InputContainer>
		);
	}
}

function NumberButton(props) {
	const idPrefix = useIdPrefix();
	const number = props.number;
	const selected = props.selected;
	let contrast = false;
	const id = props.id;

	const styleList = [styles.circleButton];

	if (selected) {
		styleList.push(styles.selectedButton);
		contrast = true;
	}

	return (
		<TouchableOpacity
			id={idPrefix + id}
			accessibilityComponentType="button"
			style={styleList}
			onPress={props.onChange}
		>
			<View style={styles.textCenter}>
				<StyledText bold primary contrast={contrast} smallHeading>
					{number}
				</StyledText>
			</View>
		</TouchableOpacity>
	);
}

function ScaleCursor(props) {
	const title = props.title;
	let alert = props.alert;
	let contrast = props.contrast;
	const styleList = [styles.arrowDown];
	if (alert) {
		alert = true;
		contrast = false;
		styleList.push(styles.arrowDownAlert);
	}
	if (contrast) {
		alert = false;
		contrast = true;
		styleList.push(styles.arrowDownPrimary);
	}
	return (
		<View style={styles.alignCenter}>
			<View style={styles.cursorRadius}>
				<StyledButton title={title} bold contrast={contrast} alert={alert} />
			</View>
			<View style={styleList} />
		</View>
	);
}

function updateValue(number) {
	const component = this;
	return function () {
		const props = component.props;
		props.defaultValue = number;
		component.setState({
			hasChanged: true,
			value: number
		});

		const onChange = props.onChange;
		if (onChange) onChange(number);
	};
}

export default function RangeInput(props) {
	const { localize, localizeFrom } = useResources();
	const idPrefix = useIdPrefix();
	return <RangeInputComp {...props} localize={localize} localizeFrom={localizeFrom} id_prefix={idPrefix} />;
}